import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Divider, Stack, Typography, styled } from '@mui/material';

import { AvailableResources, CheckResourceResponse, UnitApi } from '@/api';
import { FormRoot } from '@/components';
import { LocalStorageItems } from '@/constants';
import { WithLoaderProps, withLoader } from '@/hocs';
import { IUnitResourceForm, IUnitShiftResources, IUnitType } from '@/models';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { formatPersonName } from '@/utils';

import { AppModal, BaseModalProps, UnitResourceForm } from '../..';

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  '& .confirm-state': {
    minWidth: 520,
    maxWidth: 520,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'calc(100% - 32px)',
      maxWidth: 'calc(100% - 32px)',
    },
  },
  '& .confirm-divider': {
    marginTop: 16,
    marginBottom: 16,
  },
}));

export interface UnitResourceReallocateModalProp
  extends Omit<BaseModalProps, 'children' | 'onClose'>,
    WithLoaderProps {
  unitId: string;
  unitName?: string;
  unitType?: IUnitType;
  isForReassignment?: boolean;
  onClose: (refresh?: boolean) => void;
  currentResources?: IUnitShiftResources;
}

const UnitResourceReallocateModal: FC<UnitResourceReallocateModalProp> = ({
  showLoader,
  hideLoader,
  open,
  unitId,
  unitName,
  unitType,
  isForReassignment,
  currentResources,
  ...rest
}) => {
  const { updateToast } = useToastStore();
  const { userAgencyId } = useUserStore();
  const methods = useForm<IUnitResourceForm>();
  const { getValues, handleSubmit, reset } = methods;

  const [resources, setResources] = useState<AvailableResources>({});
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [checkResponse, setCheckResponse] =
    useState<CheckResourceResponse | null>(null);

  useEffect(() => {
    if (open) {
      if (currentResources) {
        reset({
          users: currentResources.users?.map((item) => item._id),
          vehicles: currentResources.vehicles?.map((item) => item._id),
          equipments: currentResources.equipments?.map((item) => item._id),
        });
      } else {
        reset();
      }
    }

    if (!localStorage.getItem(LocalStorageItems.CurrentAgencyId)) {
      localStorage.setItem(
        LocalStorageItems.CurrentAgencyId,
        userAgencyId || '',
      );
    }
    if (!open) {
      setCheckResponse(null);
      setConfirmOpen(false);
    }
  }, [open]);

  const getDescriptionFromResponse = () => {
    if (!checkResponse) return '';

    const { users, vehicles, equipments } = checkResponse;

    const offDutyResources: string[] = [];
    const unaffectedResources: string[] = [];

    // Process users
    Object.keys(users).forEach((userId) => {
      const u = resources.users?.find((user) => user._id === userId);
      if (u) {
        const label = formatPersonName(u);
        if (users[userId]) {
          offDutyResources.push(`${label}: ${users[userId]} will go off duty`);
        } else {
          unaffectedResources.push(`${label}: Will not affect unit`);
        }
      }
    });

    // Process vehicles
    Object.keys(vehicles).forEach((vehicleId) => {
      const v = resources.vehicles?.find(
        (vehicle) => vehicle._id === vehicleId,
      );
      if (v) {
        const label = v.name;
        if (vehicles[vehicleId]) {
          offDutyResources.push(
            `${label}: ${vehicles[vehicleId]} will go off duty`,
          );
        } else {
          unaffectedResources.push(`${label}: Will not affect unit`);
        }
      }
    });

    // Process equipments
    Object.keys(equipments).forEach((equipmentId) => {
      const e = resources.equipments?.find(
        (equipment) => equipment._id === equipmentId,
      );
      if (e) {
        const label = e.name;
        if (equipments[equipmentId]) {
          offDutyResources.push(
            `${label}: ${equipments[equipmentId]} will go off duty`,
          );
        } else {
          unaffectedResources.push(`${label}`);
        }
      }
    });

    let description = '';

    if (unaffectedResources.length > 0) {
      description +=
        'The following resources are currently assigned to other units, but their reallocation will not affect them:\n\n';
      description += unaffectedResources.join('\n');
      description += '\n\n';
    }

    if (offDutyResources.length > 0) {
      description +=
        'The following resources are currently assigned to other units, and their existing assignments will be completed before reassignment:\n\n';
      description += offDutyResources.join('\n');
      description += '\n\n';
    }

    return `${description}`;
  };

  const onSubmit = async () => {
    showLoader();
    try {
      const values = getValues();

      const res = await UnitApi.checkReallocationResource(
        unitId,
        values.users as string[],
        values.vehicles as string[],
        values.equipments as string[],
      );

      setCheckResponse(res.data);
      setConfirmOpen(true);
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message as string,
      });
    } finally {
      hideLoader();
    }
  };

  const onConfirm = () => {
    showLoader();
    const values = getValues();

    UnitApi.reallocateResources(
      unitId,
      values.users as string[],
      values.vehicles as string[],
      values.equipments as string[],
    )
      .then((res) => {
        if (res.status === 200) {
          updateToast({
            open: true,
            message: 'Resources reallocated successfully.',
            type: ConfirmTypes.SUCCESS,
          });
        }
      })
      .catch((err) => {
        updateToast({
          open: true,
          message: err.message as string,
        });
      });

    setCheckResponse(null);
    setConfirmOpen(false);

    rest.onClose(true);
    hideLoader();
  };

  const onCancel = () => {
    setCheckResponse(null);
    setConfirmOpen(false);
  };

  const confirmationDescription = getDescriptionFromResponse();

  return (
    <>
      <StyledAppModal
        title={`Reallocate resources to ${unitName}`}
        {...rest}
        open={open}
        classes={{
          content: isConfirmOpen ? 'fixed-width confirm-state' : 'fixed-width',
          divider: isConfirmOpen ? 'confirm-divider' : undefined,
        }}
      >
        <FormProvider {...methods}>
          {isConfirmOpen ? (
            <Stack spacing={1.5} sx={{ pt: 0.5, pb: 0.5 }}>
              <Typography
                sx={{
                  maxWidth: 380,
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: 'text.primary',
                }}
              >
                Do you want to proceed with reallocation?
              </Typography>

              {confirmationDescription && (
                <Typography
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {confirmationDescription}
                </Typography>
              )}

              <Stack flexDirection="row" justifyContent="flex-end" gap={1.5}>
                <Button variant="outlined" color="inherit" onClick={onCancel}>
                  Cancel
                </Button>
                <Button variant="contained" color="error" onClick={onConfirm}>
                  Confirm
                </Button>
              </Stack>
            </Stack>
          ) : (
            <FormRoot onSubmit={handleSubmit(onSubmit)}>
              <UnitResourceForm
                currentUnitType={unitType}
                resources={currentResources as AvailableResources}
                unitId={unitId}
                setOutsideResources={setResources}
                isForReassignment
                skipValidation
              />
              <Divider sx={{ my: 3, width: '100%' }} />

              <Stack flex={1} flexDirection="row" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => rest.onClose()}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="error"
                  sx={{ ml: 3, px: 2 }}
                >
                  Reallocate
                </Button>
              </Stack>
            </FormRoot>
          )}
        </FormProvider>
      </StyledAppModal>
    </>
  );
};

export default withLoader(UnitResourceReallocateModal);
